import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useForm, useFormState } from 'react-final-form';
import { Delete, CheckCircle, Cancel } from '@material-ui/icons';
import { IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import QCXDataGrid from '../../shared-components/data-grid/QCXDataGrid';
import { removeServicoByIdsAsync } from '../../features/cliente/clienteSlice';

export default function QCXClienteServicoDespesaReceitaGrid({ deletar = false, fetchByIdAsync }) {
  const { t } = useTranslation();

  const form         = useForm();
  const { values }   = useFormState();
  const dispatch     = useDispatch();
  const cliente      = useSelector((state) => state.cliente.model);

  const despesasReceitas  = values?.relacaoClienteServicoDespesaReceitas ?? [];
  const tabelasSda        = values?.relacaoClienteServicoTabelasSda ?? [];
  const retencoesImpostos = values?.relacaoClienteServicoRetencoesImpostos   // se o usuário acabou de mexer
                        ?? cliente?.relacaoClienteServicoRetencoesImpostos   // ou do modelo carregado
                        ?? [];
  const rowsMap = new Map();

  const ensureRow = (servico) => {
    if (!servico) return null;
    if (!rowsMap.has(servico.id)) {
      rowsMap.set(servico.id, {
        id: uuid(),
        servico,
        quantidadeNumerario   : 0,
        quantidadeFaturamento : 0,
        quantidadeTabelasSda  : 0,
        hasImposto            : false,
      });
    }
    return rowsMap.get(servico.id);
  };

  despesasReceitas.forEach((rel) => {
    const row = ensureRow(rel.servico);
    if (!row) return;
    if (rel.tipo === 'NUMERARIO')  row.quantidadeNumerario   += 1;
    if (rel.tipo === 'FATURAMENTO') row.quantidadeFaturamento += 1;
  });

  tabelasSda.forEach((rel) => {
    const row = ensureRow(rel.servico);
    if (row) row.quantidadeTabelasSda += 1;
  });

  retencoesImpostos.forEach((rel) => {
    const row = ensureRow(rel.servico);
    if (row) row.hasImposto = true;
  });

  const normalizedRows = Array.from(rowsMap.values());

  const columns = [
    {
      field       : 'servico',
      headerName  : 'Serviço',
      headerAlign : 'left',
      align       : 'left',
      flex        : 3,
      valueGetter : ({ row }) => row?.servico?.description ?? '-',
    },
    {
      field       : 'quantidadeNumerario',
      headerName  : 'Despesas/Receitas de Numerário',
      headerAlign : 'left',
      align       : 'left',
      flex        : 3,
      valueGetter : ({ row }) => row?.quantidadeNumerario,
    },
    {
      field       : 'quantidadeFaturamento',
      headerName  : 'Despesas/Receitas de Faturamento',
      headerAlign : 'left',
      align       : 'left',
      flex        : 3,
      valueGetter : ({ row }) => row?.quantidadeFaturamento,
    },
    {
      field       : 'hasImposto',
      headerName  : 'Impostos',
      headerAlign : 'center',
      align       : 'center',
      flex        : 1,
      sortable    : false,
      valueGetter : ({ row }) => row?.hasImposto,
      renderCell  : ({ row }) =>
        row.hasImposto ? <CheckCircle color="primary" /> : <Cancel color="error" />,
    },
    {
      field       : 'actions',
      headerName  : t('com.muralis.qcx.acoes.label'),
      headerAlign : 'center',
      hide        : !deletar,
      align       : 'center',
      flex        : 1,
      renderCell  : ({ row }) =>
        deletar && (
          <IconButton onClick={(event) => handleRemove(event, row?.servico?.id)}>
            <Delete color="error" />
          </IconButton>
        ),
    },
  ];

  const handleRemove = useCallback(
    (event, idServico) => {
      event.stopPropagation();

      dispatch(removeServicoByIdsAsync({ idCliente: cliente.id, idServico }));

      const novasDespesas = despesasReceitas.filter((r) => r.servico.id !== idServico);
      const novasTabelas  = tabelasSda       .filter((r) => r.servico.id !== idServico);
      const novasRetencoes= retencoesImpostos.filter((r) => r.servico.id !== idServico);

      form.batch(() => {
        form.change('relacaoClienteServicoDespesaReceitas', novasDespesas);
        form.change('relacaoClienteServicoTabelasSda',      novasTabelas);
        form.change('relacaoClienteServicoRetencoesImpostos', novasRetencoes);
      });
      dispatch(fetchByIdAsync(cliente.id));
    },
    [dispatch, cliente.id, despesasReceitas, tabelasSda, retencoesImpostos, form, fetchByIdAsync]
  );

  const handleRowClick = ({ row }) => {
    form.change('servico.id', row.servico.id);
  };

  return (
    <QCXDataGrid
      columns={columns}
      rows={normalizedRows}
      onRowClick={handleRowClick}
      localeText={{
        noRowsLabel: t('com.muralis.qcx.validacao.nenhumItemEncontrado'),
      }}
    />
  );
}
